/** PostHog send-boundary organization attribution, isolated to keep its policy directly testable. */

import { getActiveStudioId } from '#~/analytics/active-studio.js';
import { mapRouteToPageview } from '#~/analytics/pageview/route-mapper.js';
import {
  ORGANIZATION_GROUP_TYPE,
  namedStudioId,
  resolveEventOrganizationId
} from '#~/analytics/utils/groups-resolver.js';
import type { OrganizationInputs } from '#~/analytics/utils/groups-resolver.js';
import type { PostHogBeforeSendEvent } from '#~/posthog/envelope-enrichment.js';
import {
  POSTHOG_EVENT_PROPERTY_NAMES,
  POSTHOG_GROUP_NAMES,
  POSTHOG_INTERNAL_EVENT_NAMES
} from '#~/posthog/event-names.js';
import { logger } from '#~/logger/index.js';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const stringProperty = (properties: Record<string, unknown>, key: string): string | undefined => {
  const value = properties[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
};

/** The room a page URL shows, using the same route table as pageview tracking. */
const roomOfUrl = (url: string | undefined): string | undefined => {
  if (url === undefined) return undefined;
  try {
    return mapRouteToPageview(new URL(url).pathname).roomId;
  } catch {
    return undefined;
  }
};

/**
 * The organization inputs of a sent event.
 *
 * When the event names no room or studio of its own, the room page it was
 * captured on stands in, then the studio the session is in. The page covers
 * SDK events buffered past a navigation and events fired while a room mounts,
 * before it marks itself active; the active studio covers SDK events outside
 * the envelope allowlist, which never receive enrichment's studio stamp.
 */
const organizationInputs = (properties: Record<string, unknown>): OrganizationInputs => {
  if (namedStudioId(properties) !== undefined) {
    return properties;
  }
  const studioId =
    roomOfUrl(stringProperty(properties, POSTHOG_EVENT_PROPERTY_NAMES.CURRENT_URL)) ??
    getActiveStudioId();
  return studioId === undefined ? properties : { ...properties, studio_id: studioId };
};

/** The organization an outgoing event belongs to, or undefined when it has none. */
const resolveSentOrganizationId = (
  eventName: string,
  properties: Record<string, unknown>
): string | undefined => {
  // An organization profile update is that organization's own event, whichever
  // studio is open while the account's organizations load.
  if (
    eventName === POSTHOG_GROUP_NAMES.GROUP_IDENTIFY &&
    properties[POSTHOG_GROUP_NAMES.GROUP_TYPE] === ORGANIZATION_GROUP_TYPE
  ) {
    return stringProperty(properties, POSTHOG_GROUP_NAMES.GROUP_KEY);
  }
  return resolveEventOrganizationId(organizationInputs(properties));
};

/**
 * Set every outgoing event's organization group from the action it records.
 *
 * posthog-js otherwise fills `$groups` from a super property: whatever
 * organization `group()` last stored, persisted across page loads. That value
 * is the last organization in the account's membership list, or one left over
 * from a previous launch, and it reaches every event that does not name its own
 * groups. Replacing it here, after enrichment and before transport, covers SDK
 * events as well as tracked ones. An event with no known organization leaves
 * with no organization group at all; studio and camera groups are untouched.
 * A resolution failure must never drop the event, so it is sent with its
 * organization treated as unknown.
 */
export function attributeEventOrganization(event: PostHogBeforeSendEvent): PostHogBeforeSendEvent {
  if (
    !event ||
    typeof event.event !== 'string' ||
    // Replay transport carries no groups and is not an analytics event.
    event.event === POSTHOG_INTERNAL_EVENT_NAMES.SNAPSHOT
  ) {
    return event;
  }

  const properties = event.properties ?? {};
  let organizationId: string | undefined;
  try {
    organizationId = resolveSentOrganizationId(event.event, properties);
  } catch (error) {
    logger.error('[PostHog] Failed to resolve the event organization', error);
  }
  const sentGroups = properties[POSTHOG_GROUP_NAMES.GROUPS];
  const groups = isRecord(sentGroups) ? { ...sentGroups } : undefined;

  if (organizationId === undefined) {
    if (groups === undefined || !(ORGANIZATION_GROUP_TYPE in groups)) {
      return event;
    }
    delete groups[ORGANIZATION_GROUP_TYPE];
    return { ...event, properties: { ...properties, [POSTHOG_GROUP_NAMES.GROUPS]: groups } };
  }

  if (groups?.[ORGANIZATION_GROUP_TYPE] === organizationId) {
    return event;
  }
  return {
    ...event,
    properties: {
      ...properties,
      [POSTHOG_GROUP_NAMES.GROUPS]: { ...groups, [ORGANIZATION_GROUP_TYPE]: organizationId }
    }
  };
}
